import { getInventorySettingsStorageKey } from '../../utils/inventoryStorage';

export const startOfDay = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
};

export const isOverlappingRange = (aStart, aEnd, bStart, bEnd) => {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  const as = startOfDay(aStart).getTime();
  const ae = startOfDay(aEnd).getTime();
  const bs = startOfDay(bStart).getTime();
  const be = startOfDay(bEnd).getTime();
  return as <= be && bs <= ae;
};

export const equipmentColumns = [
  { key: 'category', labelKey: 'inventory.category' },
  { key: 'brand', labelKey: 'inventory.brand' },
  { key: 'name', labelKey: 'inventory.name' },
  { key: 'size', labelKey: 'inventory.size', width: '80px' },
  { key: 'inventory_number', labelKey: 'inventory.inventoryNumber', width: '100px' },
  { key: 'class', labelKey: 'inventory.class', width: '100px' },
  { key: 'status', labelKey: 'inventory.status', width: '140px' },
  { key: 'sku', labelKey: 'inventory.sku', width: '120px' },
  { key: 'condition', labelKey: 'inventory.condition', width: '120px' },
  { key: 'location', labelKey: 'inventory.location', width: '140px' },
  { key: 'tags', labelKey: 'inventory.tags', width: '160px' },
  { key: 'supplier', labelKey: 'inventory.supplier', width: '140px' },
  { key: 'purchaseDate', labelKey: 'inventory.purchaseDate', width: '130px' },
  { key: 'maintenanceDate', labelKey: 'inventory.maintenanceDate', width: '150px' },
  { key: 'images', labelKey: 'inventory.images', width: '140px' },
  { key: 'barcode', labelKey: 'inventory.barcode', width: '140px' },
  { key: 'currency', labelKey: 'inventory.currency', width: '120px' },
  { key: 'wearLevel', labelKey: 'inventory.wearLevel', width: '120px' },
  { key: 'compatibility', labelKey: 'inventory.compatibility', width: '160px' },
  { key: 'sizeRange', labelKey: 'inventory.sizeRange', width: '120px' }
];

// ─── Przypięta kolumna tabeli na dotyku i wąskim ekranie (V.6) ───
//
// Na telefonie i tablecie tabela przypina kolumnę zaznaczenia i PIERWSZĄ kolumnę z danymi
// (inventory.styles.js#TableWrapper), żeby po przesunięciu w bok było wiadomo, czyj jest wiersz.
// Pierwszą kolumną jest jednak Kategoria („Narty” w każdym wierszu), więc przy kolumnie Status nie
// było wiadomo, której sztuki dotyczy, a pierwszy widok tabeli nie pokazywał nazwy (test na
// iPhonie 17, 18.10.2026). Przy tym samym warunku co przypięcie kolumna nazwy (bez niej numer
// inwentarzowy) idzie na początek; na komputerze z myszą kolejność zostaje bez zmian.
export const PRZYPIETE_KOLUMNY_MEDIA = '(max-width: 768px), (hover: none) and (pointer: coarse)';

/** Kolumny tabeli z kolumną mówiącą, czyj jest wiersz (nazwa, inaczej numer), na początku. */
export function kolumnyZNazwaNaPoczatku(kolumny) {
  const lista = Array.isArray(kolumny) ? kolumny : [];
  const klucz = ['name', 'inventory_number'].find((k) => lista.some((c) => c.key === k));
  const i = klucz ? lista.findIndex((c) => c.key === klucz) : -1;
  if (i <= 0) return lista;
  return [lista[i], ...lista.slice(0, i), ...lista.slice(i + 1)];
}

// ─── Pasek filtrów: wymiary wyprowadzone z KONFIGURACJI, nie z listy na sztywno ───
//
// Magazyn obsługuje wypożyczalnie nart, rowerów, kajaków, narzędzi i namiotów, a zestaw
// pól jest per organizacja (Ustawienia magazynu → `inventory_field_settings`). Pasek filtrów
// musi więc wynikać z tej samej konfiguracji, co tabela — inaczej wypożyczalnia narzędzi
// dostaje na wierzchu „Rozmiar" i „Markę", a swoje „Zasilanie" i „Datę przeglądu" ma
// schowane w zwiniętej sekcji „Pola dodatkowe" (tak było do 2026-09-22).
//
// STAŁE są tylko trzy wymiary, bo tylko one są prawdziwe w KAŻDEJ branży: stan sprzętu,
// jego typ i termin, w którym ma być wolny. Reszta pojawia się dokładnie wtedy, gdy
// organizacja ma taką kolumnę włączoną (wywołujący dokłada jeszcze warunek „są dane,
// po których można filtrować" — pusta lista wartości to pigułka, która nic nie robi).
export const ALWAYS_ON_FILTER_KEYS = ['status', 'category', 'availability'];

// Kolumny, które CELOWO nie stają się pigułką filtra:
// `name`/`inventory_number` obsługuje jedno pole szukania (osobne pigułki dublowałyby je),
// a `images` to lista adresów — filtrowanie po niej nie znaczy nic dla człowieka, choć do
// 2026-09-22 było oferowane jako zwykły filtr tekstowy.
export const NON_FILTERABLE_COLUMN_KEYS = new Set(['name', 'inventory_number', 'images']);

// Filtry trzymane na WIERZCHU obiektu `filters` (reszta siedzi w `filters.dynamic`).
export const TOP_LEVEL_FILTER_KEYS = new Set(['status', 'category', 'brand', 'class']);

export function buildFilterDimensions({ visibleColumns, columns } = {}) {
  const lista = Array.isArray(columns) ? columns : equipmentColumns;
  const widoczne = new Set(Array.isArray(visibleColumns) ? visibleColumns : []);

  const wymiary = [
    { key: 'status', kind: 'status', labelKey: 'inventory.statusFilter' },
    { key: 'category', kind: 'category', labelKey: 'inventory.category' },
    { key: 'availability', kind: 'availability', labelKey: 'inventory.availability' },
  ];
  const juzJest = new Set(wymiary.map((w) => w.key));

  lista.forEach((col) => {
    if (!col || !col.key) return;
    if (juzJest.has(col.key)) return;
    if (NON_FILTERABLE_COLUMN_KEYS.has(col.key)) return;
    if (!widoczne.has(col.key)) return;
    // `size` ma własny rodzaj, bo jako jedyny przyjmuje ZAKRES obok wartości dokładnej.
    const kind = col.key === 'size' ? 'size' : dynamicFilterType(col.key, col.fieldType);
    wymiary.push({ key: col.key, kind, labelKey: col.labelKey, label: col.label });
    juzJest.add(col.key);
  });

  return wymiary;
}

// ─── Kolumny własne organizacji w tabeli magazynu (24.10.2026) ───
//
// Ustawienia magazynu pozwalały założyć własne pole (np. „Powierzchnia żagla”), włączyć je jako
// widoczne, wypełnić w formularzu i importem — a tabela magazynu i tak go nie pokazywała, bo znała
// wyłącznie kolumny wbudowane. Wartość leży w `metadata[key]` (tam zapisuje ją formularz i import),
// a etykieta to literalny tekst nadany przez firmę (dane, nie klucz tłumaczenia).
const ZAJETE_KLUCZE_KOLUMN = new Set([...equipmentColumns.map((c) => c.key), 'price', 'bail', 'price_over_7']);

export function customTableColumns(customColumns) {
  const lista = Array.isArray(customColumns) ? customColumns : [];
  const juz = new Set();
  return lista
    .filter((c) => c && c.key && !ZAJETE_KLUCZE_KOLUMN.has(c.key) && !juz.has(c.key) && juz.add(c.key))
    .map((c) => ({ key: c.key, label: c.label || c.key, fieldType: c.fieldType || 'text', custom: true }));
}

/** Rodzaj filtra kolumny: wbudowana z mapy, własna z typu pola nadanego w ustawieniach. */
export function dynamicFilterType(key, fieldType) {
  if (DYNAMIC_FILTER_TYPE_BY_KEY[key]) return DYNAMIC_FILTER_TYPE_BY_KEY[key];
  if (fieldType === 'date') return 'date';
  if (fieldType === 'number') return 'number';
  return 'text';
}

/** Nagłówek kolumny: własna niesie literalną etykietę, wbudowana klucz tłumaczenia. */
export function columnLabel(t, col) {
  if (!col) return '';
  if (col.label) return col.label;
  return (col.labelKey && t(col.labelKey)) || col.key;
}

/** Tekst komórki kolumny własnej: lista jako „a, b”, data bez godziny, brak wartości jako pusto. */
export function formatCustomCell(value, fieldType) {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean).join(', ');
  if (typeof value === 'boolean') return value ? '✓' : '';
  if (typeof value === 'object') return '';
  const tekst = String(value).trim();
  if (fieldType === 'date') return tekst.slice(0, 10);
  return tekst;
}

// Co pokazać NA pigułce, gdy filtr jest ustawiony (albo `null`, gdy nie jest). Pigułka jest
// jednocześnie kontrolką i podsumowaniem — stąd wartość musi dać się przeczytać w jednym
// słowie, bez otwierania czegokolwiek.
export function activeFilterSummary(key, filters, options) {
  const f = filters || {};
  const formatDate = (options && options.formatDate) || ((d) => String(d));

  if (key === 'availability') {
    const od = f.dateFrom ? formatDate(f.dateFrom) : '';
    const doD = f.dateTo ? formatDate(f.dateTo) : '';
    if (od && doD) return `${od} – ${doD}`;
    return od || null;
  }

  if (key === 'size') {
    const min = String(f.minSize == null ? '' : f.minSize).trim();
    const max = String(f.maxSize == null ? '' : f.maxSize).trim();
    if (min && max) return `${min}–${max}`;
    if (min) return `≥ ${min}`;
    if (max) return `≤ ${max}`;
    const dokladny = String(f.size == null ? '' : f.size).trim();
    return dokladny || null;
  }

  if (TOP_LEVEL_FILTER_KEYS.has(key)) {
    const v = String(f[key] == null ? '' : f[key]).trim();
    return v || null;
  }

  const dyn = f.dynamic || {};
  const v = String(dyn[key] == null ? '' : dyn[key]).trim();
  return v || null;
}

// Łatka czyszcząca JEDEN wymiar. `null` znaczy „to filtr dynamiczny" — wołający zdejmuje
// go przez `setDynamicFilter`, bo tamte żyją w zagnieżdżonym obiekcie.
export function clearFilterPatch(key) {
  if (key === 'availability') return { dateFrom: null, dateTo: null };
  if (key === 'size') return { size: '', minSize: '', maxSize: '' };
  if (TOP_LEVEL_FILTER_KEYS.has(key)) return { [key]: '' };
  return null;
}

// Ile filtrów jest włączonych — zakres dat liczy się jako JEDEN, bo na pasku jest jedną
// pigułką (dawny licznik przy przycisku „Filtry" liczył `dateFrom` i `dateTo` osobno,
// więc wybranie jednego okresu podbijało odznakę o dwa).
export function countActiveFilters(filters, dimensions) {
  const wymiary = Array.isArray(dimensions) && dimensions.length
    ? dimensions
    : buildFilterDimensions({ visibleColumns: [] });
  const zWymiarow = wymiary.filter((d) => activeFilterSummary(d.key, filters) !== null).length;
  const szukanie = String((filters && filters.search) || '').trim() ? 1 : 0;
  return zWymiarow + szukanie;
}

export const BASE_INVENTORY_COLUMN_KEYS = new Set([
  'category',
  'brand',
  'name',
  'size',
  'inventory_number',
  'class',
  'status'
]);

export const DYNAMIC_FILTER_TYPE_BY_KEY = {
  purchaseDate: 'date',
  maintenanceDate: 'date',
  currency: 'text',
  wearLevel: 'text',
  sizeRange: 'text',
  condition: 'text',
  location: 'text',
  tags: 'text',
  supplier: 'text',
  sku: 'text',
  barcode: 'text',
  images: 'text',
  compatibility: 'text'
};

export const FIELD_ALIASES = {
  purchaseDate: 'purchase_date',
  maintenanceDate: 'maintenance_date',
  priceCurrency: 'price_currency',
  depositCurrency: 'deposit_currency',
  currency: ['price_currency', 'deposit_currency', 'priceCurrency', 'depositCurrency', 'currency'],
  wearLevel: 'wear_level',
  sizeRange: 'size_range',
  // Bail/deposit can appear under many names in different backends or metadata
  bail: ['deposit', 'deposit_amount', 'deposit_amount_raw', 'deposit_override', 'deposit_override_raw', 'kaucja', 'bail', 'pricing.deposit', 'pricing.deposit_amount', 'display_values.deposit', 'display_values.deposit_amount']
};

// Deep search helper: find a key anywhere inside an object tree (limited depth)
export function deepFind(root, targetKey, maxDepth = 6) {
  const seen = new Set();
  function _dfs(node, depth) {
    if (node == null || depth > maxDepth) return undefined;
    if (typeof node !== 'object') return undefined;
    if (seen.has(node)) return undefined;
    seen.add(node);
    if (Object.prototype.hasOwnProperty.call(node, targetKey)) return node[targetKey];
    for (const k of Object.keys(node)) {
      const v = node[k];
      if (typeof v === 'object') {
        const found = _dfs(v, depth + 1);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  }
  return _dfs(root, 0);
}

export function resolveInventoryValue(obj, key) {
  if (!obj) return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const meta = obj.metadata && typeof obj.metadata === 'object' ? obj.metadata : null;
  if (meta && Object.prototype.hasOwnProperty.call(meta, key)) return meta[key];
  const alias = FIELD_ALIASES[key];
  if (alias) {
    const tryPath = (root, path) => {
      if (root == null) return undefined;
      if (Object.prototype.hasOwnProperty.call(root, path)) return root[path];
      if (String(path).includes('.')) {
        const parts = String(path).split('.');
        let cur = root;
        for (const p of parts) {
          if (cur == null || typeof cur !== 'object' || !Object.prototype.hasOwnProperty.call(cur, p)) {
            cur = undefined;
            break;
          }
          cur = cur[p];
        }
        return cur;
      }
      return undefined;
    };

    if (Array.isArray(alias)) {
      for (const a of alias) {
        const v1 = tryPath(obj, a);
        if (v1 !== undefined) return v1;
        const v2 = tryPath(meta, a);
        if (v2 !== undefined) return v2;
      }
    } else {
      const v1 = tryPath(obj, alias);
      if (v1 !== undefined) return v1;
      const v2 = tryPath(meta, alias);
      if (v2 !== undefined) return v2;
    }
  }
  // Final fallback: search deeply inside object/meta for key or alias names
  const deepVal = deepFind(obj, key) ?? (meta ? deepFind(meta, key) : undefined);
  if (deepVal !== undefined) return deepVal;
  if (alias) {
    if (Array.isArray(alias)) {
      for (const a of alias) {
        const deepA = deepFind(obj, a) ?? (meta ? deepFind(meta, a) : undefined);
        if (deepA !== undefined) return deepA;
      }
    } else {
      const deepA = deepFind(obj, alias) ?? (meta ? deepFind(meta, alias) : undefined);
      if (deepA !== undefined) return deepA;
    }
  }
  return undefined;
}

export const defaultVisibleColumns = equipmentColumns.map(c => c.key);

export const loadInventorySettings = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(getInventorySettingsStorageKey());
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

// E.5: brand/size są NULL-able w bazie (część branż ich nie ma) — spójny placeholder
// w tabeli magazynu zamiast pustej komórki.
export const withEmptyDash = (value) => (value == null || value === '' ? '—' : value);

export const formatSizeRangeLabel = (start, end) => (
  start >= 40
    ? `${start} - ${end}`
    : `${start.toFixed(1)} - ${end.toFixed(1)}`
);

// U.15: pure view-model for the optional "Cena za dzień" column.
// GET /api/equipment zwraca stawkę jako `price_per_day` (number), więc
// próbujemy po kolei znanych nazw; pusta wartość → '' (kolumna nie pokazuje 0-zł-śmiecia).
// P8.1d: szablon sufiksu jest WSTRZYKIWANY, bo ta sama stawka renderuje się w DWÓCH
// kolumnach — „Cena" (tutaj) i „Cena za dzień przy pobycie > 7 dni" (pages/inventory/index.js),
// a ta druga idzie już przez t('priceBreakdown.perDay'). Bez wspólnego szablonu jedna kolumna
// mówiłaby „/dzień", a druga „/day" w tym samym wierszu tabeli. Wartość domyślna zachowuje
// dotychczasowe zachowanie, więc moduł nadal testuje się bez Reacta i bez słownika.
export const PER_DAY_TEMPLATE_PL = '{amount}/dzień';
export const formatDailyPrice = (e, perDayTemplate = PER_DAY_TEMPLATE_PL) => {
  const val = resolveInventoryValue(e, 'price')
    ?? resolveInventoryValue(e, 'price_per_day')
    ?? resolveInventoryValue(e, 'pricePerDay')
    ?? resolveInventoryValue(e, 'cena')
    ?? resolveInventoryValue(e, 'price_per_day_raw');
  if (val == null || val === '') return '';
  const num = Number(val) || 0;
  const formatted = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 }).format(num);
  const currency = resolveInventoryValue(e, 'currency')
    || resolveInventoryValue(e, 'priceCurrency')
    || resolveInventoryValue(e, 'price_currency')
    || 'PLN';
  return perDayTemplate.replace('{amount}', `${formatted} ${String(currency).toUpperCase()}`);
};

// U.15: predykat filtra po klasie sprzętu (pusty filtr = przepuść wszystko).
export const itemMatchesClass = (item, cls) => {
  if (!cls) return true;
  return item?.class === cls;
};

// U.15: wartość do sortowania kolumny. Kolumna 'price' przychodzi z /api/equipment
// jako alias `price_per_day` (routes/equipment.js), więc komparator musi czytać
// właściwe pole — inaczej sort po „Cena za dzień" nic nie robi (undefined vs undefined).
export const sortValueForColumn = (item, key) => {
  const resolvedKey = key === 'price' ? 'price_per_day' : key;
  const wprost = item?.[resolvedKey];
  // Pola z `metadata` (SKU, stan, kolumny własne) nie leżą na wierzchu wiersza, więc bez tego
  // sortowanie po nich nie zmieniało kolejności niczego.
  return wprost !== undefined ? wprost : resolveInventoryValue(item, key);
};
